import { useEffect, useRef, useState } from "react";
import { referralsApi, type ReferralStats } from "../auth/api";
import { useSolUsdPrice } from "../hooks/useSolUsdPrice";
import { SOLANA_NETWORK, LAMPORTS_PER_SOL } from "../contracts/solana/config";

/* ============================================================
   Refer & Earn panel — new "Refer Card" design from the
   Claude Design "Hupper - Pong" handoff (Refer Card.html),
   same visual language as the Wallet card.

   Draggable floating panel. The visuals come straight from the
   handoff CSS (scoped under `.rc-*` with the bundle's warm
   palette + `rc-` keyframes so nothing leaks). All behaviour is
   preserved from the previous panel: drag, Escape-to-close, live
   referral stats from /api/referrals/me (30s refresh), copy
   link/code with green-check feedback, and native share.
   ============================================================ */

interface ReferEarnPanelProps {
  walletAddress: string | null;
  /** Server-side auth token from useAuth — required to fetch real
   *  referral stats. Without it the panel falls back to zeros. */
  apiToken: string | null;
  onClose: () => void;
}

const PANEL_WIDTH = 380;

function buildReferralLink(address: string | null): string {
  if (!address) return `${window.location.origin}/?ref=preview`;
  // 10-char short referral code derived from the address
  const code = address.replace(/^0x/, "").slice(0, 10);
  return `${window.location.origin}/?ref=${code}`;
}

function defaultPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 0, y: 80 };
  return {
    x: Math.max(16, window.innerWidth - PANEL_WIDTH - 24),
    y: 80,
  };
}

// ── Inline icons (match the handoff SVGs) ───────────────────────────
const GiftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M20 12v9H4v-9" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M22 7H2v5h20V7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 22V7M12 7H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M20 6L9 17l-5-5" stroke="#5fcf80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ShareIcon = ({ stroke = "currentColor", size = 13 }: { stroke?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="3" stroke={stroke} strokeWidth="2" />
    <circle cx="6" cy="12" r="3" stroke={stroke} strokeWidth="2" />
    <circle cx="18" cy="19" r="3" stroke={stroke} strokeWidth="2" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const RC_STYLES = `
.rc-card {
  --bg:#0a0806; --card:#0e0c0a; --card-2:#14110e; --line:#211c16; --line-2:#2d251c;
  --text:#f5efe6; --muted:#8a7f72; --muted-2:#5a5247; --accent:#ff5722; --accent-2:#ff8a4a;
  --green:#5fcf80;
  width: 380px;
  border-radius: 18px;
  background:
    radial-gradient(ellipse 80% 40% at 50% 0%, rgba(255, 87, 34, 0.06), transparent 70%),
    linear-gradient(180deg, rgba(20, 17, 14, 0.95), rgba(10, 8, 6, 0.98));
  box-shadow: 0 40px 100px -30px rgba(0,0,0,0.6), 0 0 0 1px var(--line-2), inset 0 1px 0 rgba(255,138,74,0.06);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(12px);
  color: var(--text);
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
}
.rc-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent 0%, var(--accent) 30%, var(--accent-2) 50%, var(--accent) 70%, transparent 100%);
  opacity: 0.85; box-shadow: 0 0 12px rgba(255,87,34,0.4);
}
.rc-card::after {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; width: 80px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
  animation: rc-ambientSweep 4.5s linear infinite; pointer-events: none;
}
.rc-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid var(--line);
}
.rc-header-left { display: flex; align-items: center; gap: 12px; }
.rc-grip {
  display: grid; grid-template-columns: repeat(2, 3px); grid-template-rows: repeat(3, 3px);
  gap: 3px; opacity: 0.45; transition: opacity 160ms ease;
}
.rc-header-left:hover .rc-grip { opacity: 0.8; }
.rc-grip span { width: 3px; height: 3px; border-radius: 999px; background: var(--muted); }
.rc-gift-icon {
  width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center;
  color: var(--accent); animation: rc-giftBob 3s ease-in-out infinite;
  filter: drop-shadow(0 0 12px rgba(255,87,34,0.35));
}
.rc-header-title { font-size: 14.5px; font-weight: 600; color: var(--text); letter-spacing: -0.2px; }
.rc-header-close {
  width: 30px; height: 30px; border-radius: 999px; border: 1px solid var(--line-2);
  background: transparent; color: var(--muted); display: inline-flex; align-items: center;
  justify-content: center; cursor: pointer; transition: all 160ms ease;
}
.rc-header-close:hover { color: var(--text); border-color: var(--muted); background: rgba(255,255,255,0.03); }
.rc-body { padding: 18px 20px 22px; display: flex; flex-direction: column; gap: 16px; flex: 1 1 auto; min-height: 0; overflow-y: auto; }
/* Children keep their natural height so a tall card scrolls instead of
   compressing the overflow:hidden hero (which would clip its text). */
.rc-body > * { flex-shrink: 0; }
.rc-hero {
  position: relative; border-radius: 14px; padding: 18px 20px;
  background:
    radial-gradient(ellipse 80% 60% at 100% 0%, rgba(255,87,34,0.10), transparent 70%),
    linear-gradient(180deg, rgba(28,22,18,0.6), rgba(14,11,9,0.7));
  border: 1px solid rgba(255,87,34,0.22); overflow: hidden;
}
.rc-corner-line {
  position: absolute; top: 0; left: 14px; right: 14px; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,138,74,0.5), transparent);
}
.rc-hero-label { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.rc-hero-label-text { font-family: 'Geist Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 1.8px; color: var(--muted); font-weight: 600; }
.rc-hero-tier {
  font-family: 'Geist Mono', ui-monospace, monospace; font-size: 9.5px; letter-spacing: 1.2px;
  color: var(--accent); font-weight: 600; padding: 3px 8px; border-radius: 4px;
  background: rgba(255,87,34,0.08); border: 1px solid rgba(255,87,34,0.3);
}
.rc-hero-value { display: flex; align-items: baseline; gap: 6px; margin-bottom: 10px; }
.rc-hero-pct {
  font-family: 'Geist Mono', ui-monospace, monospace; font-size: 52px; font-weight: 700;
  color: var(--text); letter-spacing: -2px; line-height: 1.1; padding-bottom: 2px;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg, #fff8ef 0%, #ffd1ad 100%); background-clip: text;
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  text-shadow: 0 0 50px rgba(255,140,80,0.2);
}
.rc-hero-pct-sign { font-family: 'Geist Mono', ui-monospace, monospace; font-size: 18px; color: var(--accent); font-weight: 600; transform: translateY(-18px); }
.rc-hero-desc { font-size: 12px; color: var(--muted); line-height: 1.5; }
.rc-hero-desc b { color: var(--text); font-weight: 500; }
.rc-sect-label {
  display: flex; align-items: center; justify-content: space-between;
  font-family: 'Geist Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 1.8px;
  color: var(--muted-2); font-weight: 600; margin-top: -2px; margin-bottom: -10px;
}
.rc-input-row {
  display: flex; align-items: center; border-radius: 10px; border: 1px solid var(--line);
  background: rgba(255,255,255,0.014); padding: 0 4px 0 12px; height: 40px; transition: border-color 160ms ease;
}
.rc-input-row:hover { border-color: var(--line-2); }
.rc-code-row { height: 48px; padding-left: 14px; }
.rc-input-value {
  flex: 1; min-width: 0; font-family: 'Geist Mono', ui-monospace, monospace; font-size: 12px;
  color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.1px;
}
/* No text-transform: the short code is case-sensitive base58 on Solana —
   uppercasing it would show a code that doesn't match what's copied/shared. */
.rc-code-row .rc-input-value { font-size: 18px; font-weight: 600; letter-spacing: 1.5px; }
.rc-icon-btn {
  width: 30px; height: 30px; border-radius: 6px; border: none; background: transparent;
  color: var(--muted); cursor: pointer; display: inline-flex; align-items: center;
  justify-content: center; transition: all 160ms ease; flex-shrink: 0;
}
.rc-icon-btn:hover { color: var(--text); background: rgba(255,255,255,0.04); }
.rc-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
.rc-stat {
  padding: 12px 14px; border-radius: 10px; border: 1px solid var(--line);
  background: rgba(255,255,255,0.012); display: flex; flex-direction: column; gap: 5px;
}
.rc-stat-label { font-family: 'Geist Mono', ui-monospace, monospace; font-size: 9.5px; letter-spacing: 1.6px; color: var(--muted-2); font-weight: 600; }
.rc-stat-value {
  font-family: 'Geist Mono', ui-monospace, monospace; font-size: 22px; font-weight: 700;
  color: var(--text); letter-spacing: -0.4px; line-height: 1; font-variant-numeric: tabular-nums;
}
.rc-stat-value.rc-earned { color: var(--green); }
.rc-stats-error { font-family: 'Geist Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.4px; color: #d9a441; margin-top: -6px; }
.rc-how { border-radius: 12px; padding: 14px 16px 16px; border: 1px solid var(--line); background: rgba(255,255,255,0.014); }
.rc-how-label { font-family: 'Geist Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 1.8px; color: var(--muted-2); font-weight: 600; margin-bottom: 10px; }
.rc-how-step {
  display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; font-size: 12.5px;
  color: var(--muted); line-height: 1.45; border-top: 1px solid var(--line);
}
.rc-how-step:first-of-type { border-top: none; padding-top: 4px; }
.rc-how-step:last-of-type { padding-bottom: 0; }
.rc-how-step b { color: var(--text); font-weight: 500; }
.rc-how-step .rc-accent { color: var(--accent); font-weight: 600; font-family: 'Geist Mono', ui-monospace, monospace; }
.rc-num {
  flex-shrink: 0; width: 20px; height: 20px; border-radius: 6px; background: rgba(255,87,34,0.08);
  border: 1px solid rgba(255,87,34,0.25); color: var(--accent); font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px;
}
.rc-share-btn {
  appearance: none; border: none; border-radius: 12px; cursor: pointer; height: 48px;
  background: linear-gradient(180deg, #ff7b3d 0%, #ff5722 55%, #e94a18 100%); color: #fff;
  font-family: inherit; font-size: 14px; font-weight: 600; letter-spacing: 0.1px;
  display: flex; align-items: center; justify-content: center; gap: 10px; position: relative;
  overflow: hidden; margin-top: 4px;
  box-shadow: 0 10px 30px -14px rgba(255,87,34,0.6), 0 0 0 1px rgba(255,140,80,0.25) inset, 0 1px 0 rgba(255,255,255,0.15) inset;
  transition: all 180ms ease;
}
.rc-share-btn::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 50%; background: linear-gradient(180deg, rgba(255,255,255,0.18), transparent); pointer-events: none; }
.rc-share-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 50px -18px rgba(255,87,34,0.7), 0 0 0 1px rgba(255,140,80,0.4) inset, 0 1px 0 rgba(255,255,255,0.15) inset;
}
@keyframes rc-ambientSweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes rc-giftBob { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-2px) rotate(3deg); } }
@media (prefers-reduced-motion: reduce) {
  .rc-card::after, .rc-gift-icon { animation: none; }
}
`;

export default function ReferEarnPanel({ walletAddress, apiToken, onClose }: ReferEarnPanelProps) {
  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  // SOL/USD spot price — renders Solana earnings (lamports) in USD, the same
  // source ProfilePage/header use. Unused on EVM (amounts are USDC, USD-pegged).
  const { price: solPrice } = useSolUsdPrice();

  // Refresh every 30 s while the panel is open so a settle that just
  // landed shows up without the user needing to re-open.
  useEffect(() => {
    if (!apiToken) return;
    let cancelled = false;
    const refresh = () => {
      referralsApi
        .me(apiToken)
        .then((r) => {
          if (!cancelled) {
            setStats(r);
            setStatsError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setStatsError(err?.message ?? "stats unavailable");
        });
    };
    refresh();
    const id = window.setInterval(refresh, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [apiToken]);

  // Server's code (canonical) takes precedence over the locally-derived
  // one — they should match in practice, but the server is source of truth.
  const referralCode = stats?.code ?? buildReferralLink(walletAddress).split("ref=")[1] ?? "preview";
  const referralLink =
    typeof window !== "undefined"
      ? `${window.location.origin}/?ref=${referralCode}`
      : `/?ref=${referralCode}`;
  // Display without the protocol (cleaner, matches the design); copy/share
  // still use the full URL.
  const displayLink = referralLink.replace(/^https?:\/\//, "");
  const cutPct = stats ? (stats.cutBps / 100).toFixed(0) : "40";
  // Earnings label: on Solana the credits are lamports → convert to USD with the
  // live price (falling back to a SOL figure until it loads); on EVM they're
  // USD-pegged USDC so we show the server's "$" figure directly.
  const earnedDisplay = (() => {
    if (SOLANA_NETWORK) {
      const sol = Number(stats?.earnedMinor ?? "0") / LAMPORTS_PER_SOL;
      if (solPrice == null) {
        return `${sol.toLocaleString("en-US", { maximumFractionDigits: 4 })} SOL`;
      }
      return `$${(sol * solPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${stats?.earned ?? "0.00"}`;
  })();
  const currencyLabel = SOLANA_NETWORK ? "SOL" : "USDC";
  const networkLabel = SOLANA_NETWORK ? "Solana" : "Polygon";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      setPos({
        x: Math.max(-PANEL_WIDTH + 80, Math.min(newX, window.innerWidth - 80)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 60)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  const startDrag = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      /* clipboard blocked — still show feedback */
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1500);
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
    } catch {
      /* clipboard blocked — still show feedback */
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 1500);
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Hupper",
          text: `Skill-based wagering on ${networkLabel}. Use my code:`,
          url: referralLink,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  return (
    <>
      <style>{RC_STYLES}</style>
      <div
        role="dialog"
        aria-label="Refer & Earn"
        className="rc-card"
        style={{
          position: "fixed",
          zIndex: 50,
          left: pos.x,
          top: pos.y,
          width: "min(380px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 96px)",
          userSelect: dragging ? "none" : "auto",
        }}
      >
        {/* Header (drag handle) */}
        <div
          className="rc-header"
          onMouseDown={startDrag}
          style={{ cursor: dragging ? "grabbing" : "grab" }}
        >
          <div className="rc-header-left">
            <span className="rc-grip" aria-hidden="true">
              <span /><span /><span /><span /><span /><span />
            </span>
            <span className="rc-gift-icon">
              <GiftIcon />
            </span>
            <span className="rc-header-title">Refer &amp; Earn</span>
          </div>
          <button data-no-drag onClick={onClose} aria-label="Close" className="rc-header-close">
            <CloseIcon />
          </button>
        </div>

        <div className="rc-body">
          {/* Hero — your cut */}
          <div className="rc-hero">
            <div className="rc-corner-line" />
            <div className="rc-hero-label">
              <span className="rc-hero-label-text">YOUR CUT</span>
              <span className="rc-hero-tier">REFERRER · TIER 1</span>
            </div>
            <div className="rc-hero-value">
              <span className="rc-hero-pct">{cutPct}</span>
              <span className="rc-hero-pct-sign">%</span>
            </div>
            <div className="rc-hero-desc">
              Of every <b>match fee</b> paid by players you bring in. Settles in{" "}
              <b>batched {currencyLabel}</b> payouts.
            </div>
          </div>

          {/* Your link */}
          <div className="rc-sect-label">
            <span>YOUR LINK</span>
          </div>
          <div className="rc-input-row">
            <span className="rc-input-value" title={referralLink}>{displayLink}</span>
            <button data-no-drag onClick={copyLink} className="rc-icon-btn" aria-label="Copy link" title="Copy">
              {copiedLink ? <CheckIcon /> : <CopyIcon />}
            </button>
            <button data-no-drag onClick={share} className="rc-icon-btn" aria-label="Share" title="Share">
              <ShareIcon />
            </button>
          </div>

          {/* Short code */}
          <div className="rc-sect-label">
            <span>SHORT CODE</span>
          </div>
          <div className="rc-input-row rc-code-row">
            <span className="rc-input-value">{referralCode}</span>
            <button data-no-drag onClick={copyCode} className="rc-icon-btn" aria-label="Copy code" title="Copy">
              {copiedCode ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>

          {/* Stats */}
          <div className="rc-stats">
            <div className="rc-stat">
              <span className="rc-stat-label">INVITED</span>
              <span className="rc-stat-value">{stats?.invited ?? 0}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat-label">ACTIVE</span>
              <span className="rc-stat-value">{stats?.active ?? 0}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat-label">EARNED</span>
              <span className="rc-stat-value rc-earned">{earnedDisplay}</span>
            </div>
          </div>
          {statsError && <div className="rc-stats-error">Stats unavailable · {statsError}</div>}

          {/* How it works */}
          <div className="rc-how">
            <div className="rc-how-label">HOW IT WORKS</div>
            <div className="rc-how-step">
              <span className="rc-num">1</span>
              <div>Friend signs up via your <b>link</b> or <b>code</b>.</div>
            </div>
            <div className="rc-how-step">
              <span className="rc-num">2</span>
              <div>They play matches — you collect <span className="rc-accent">{cutPct}%</span> of the house fee.</div>
            </div>
            <div className="rc-how-step">
              <span className="rc-num">3</span>
              <div>Settles <b>on-chain</b> after each match. No claim button.</div>
            </div>
          </div>

          {/* Share CTA */}
          <button data-no-drag onClick={share} className="rc-share-btn">
            <ShareIcon stroke="#fff" size={15} />
            Share your link
          </button>
        </div>
      </div>
    </>
  );
}
